import React, { useEffect, useState } from "react";
import { api, formatError } from "@/api/client";
import { Plus, X, Trash, PencilSimple, Buildings, UserCircle, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { key: "companies", label: "Companies / Firms", icon: Buildings, endpoint: "/companies" },
  { key: "clients", label: "Clients", icon: UserCircle, endpoint: "/clients" },
  { key: "vendors", label: "Vendors", icon: Truck, endpoint: "/vendors" },
];

export default function MasterData() {
  const [tab, setTab] = useState("companies");

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="masters-page">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Reference</div>
        <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">Master Data</h1>
        <p className="text-sm text-neutral-500 mt-1">Companies, clients and vendors used across sales &amp; billing.</p>
      </div>

      <div className="border-b border-neutral-200 flex gap-6 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`master-tab-${t.key}`} onClick={() => setTab(t.key)} className={`pb-3 text-sm font-medium flex items-center gap-1.5 transition-colors duration-200 border-b-2 ${tab === t.key ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {TABS.map((t) => tab === t.key && <MasterTable key={t.key} kind={t.key} endpoint={t.endpoint} />)}
    </div>
  );
}

function MasterTable({ kind, endpoint }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`${endpoint}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid={`new-${kind}-btn`} onClick={() => setOpen("new")} className="dos-btn dos-btn-primary"><Plus size={14} /> New</button>
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Name</th>
              {kind !== "companies" && <th className="px-4 py-3">Contact</th>}
              <th className="px-4 py-3">GST</th>
              <th className="px-4 py-3">PAN</th>
              <th className="px-4 py-3">Phone / Email</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors duration-200">
                <td className="px-4 py-3 font-medium text-neutral-900">{i.name}</td>
                {kind !== "companies" && <td className="px-4 py-3 text-neutral-600">{i.contact_person || "—"}</td>}
                <td className="px-4 py-3 font-mono text-xs text-neutral-600">{i.gst || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-600">{i.pan || "—"}</td>
                <td className="px-4 py-3 text-neutral-600 text-xs">{i.phone || "—"}<br/>{i.email || ""}</td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <button onClick={() => setOpen(i)} data-testid={`edit-${kind}-${i.id}`} className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={14} /></button>
                  <button onClick={() => del(i.id)} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-neutral-500 text-sm">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <MasterModal kind={kind} endpoint={endpoint} record={open === "new" ? null : open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

// Stable, memoized field component defined at module scope so React does not
// unmount / remount the input on every keystroke (which caused focus loss).
const Field = React.memo(function Field({ testId, label, type = "text", value, onChange, full, rows }) {
  const cls = full ? "col-span-2" : "";
  return (
    <div className={cls}>
      <label className="text-xs text-neutral-500">{label}</label>
      {rows ? (
        <textarea data-testid={testId} value={value || ""} onChange={onChange} className="dos-input mt-1" rows={rows} />
      ) : (
        <input data-testid={testId} type={type} value={value || ""} onChange={onChange} className="dos-input mt-1" />
      )}
    </div>
  );
});

function MasterModal({ kind, endpoint, record, onClose, onSaved }) {
  const isNew = !record;
  const [form, setForm] = useState(record || {
    name: "", address: "", gst: "", pan: "", email: "", phone: "",
    ...(kind === "companies" ? { logo: "", letterhead: "", cin: "", bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "", upi_id: "", authorized_signatory: "", invoice_prefix: "INV", wo_prefix: "WO" } : {}),
    ...(kind === "clients" ? { contact_person: "", billing_address: "", shipping_address: "", payment_terms: "Net 30", credit_limit: 0, notes: "" } : {}),
    ...(kind === "vendors" ? { contact_person: "", bank_name: "", bank_account: "", bank_ifsc: "", material_categories: [], notes: "" } : {}),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (isNew) await api.post(endpoint, form);
      else await api.patch(`${endpoint}/${record.id}`, form);
      toast.success(isNew ? "Created" : "Updated"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  const uploadImage = async (fieldKey, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [fieldKey]: data.url }));
      toast.success("Uploaded");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-2xl my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold text-neutral-900">{isNew ? "New" : "Edit"} {kind === "companies" ? "Company / Firm" : kind === "clients" ? "Client" : "Vendor"}</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs text-neutral-500">Name</label><input data-testid={`${kind}-name`} required value={form.name || ""} onChange={set("name")} className="dos-input mt-1" /></div>
          {kind !== "companies" && <Field testId={`${kind}-contact_person`} label="Contact Person" value={form.contact_person} onChange={set("contact_person")} />}
          <Field testId={`${kind}-phone`} label="Phone" value={form.phone} onChange={set("phone")} />
          <Field testId={`${kind}-email`} label="Email" type="email" value={form.email} onChange={set("email")} />
          <Field testId={`${kind}-gst`} label="GSTIN" value={form.gst} onChange={set("gst")} />
          <Field testId={`${kind}-pan`} label="PAN" value={form.pan} onChange={set("pan")} />
          {kind === "companies" && <Field testId={`${kind}-cin`} label="CIN" value={form.cin} onChange={set("cin")} />}
          <div className="col-span-2"><label className="text-xs text-neutral-500">Address</label><textarea value={form.address || ""} onChange={set("address")} className="dos-input mt-1" rows={2} /></div>
          {kind === "clients" && <>
            <div className="col-span-2"><label className="text-xs text-neutral-500">Billing Address</label><textarea value={form.billing_address || ""} onChange={set("billing_address")} className="dos-input mt-1" rows={2} /></div>
            <Field testId={`${kind}-payment_terms`} label="Payment Terms" value={form.payment_terms} onChange={set("payment_terms")} />
            <Field testId={`${kind}-credit_limit`} label="Credit Limit" type="number" value={form.credit_limit} onChange={set("credit_limit")} />
          </>}
          {(kind === "companies" || kind === "vendors") && <>
            <div className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">Bank Details</div>
            <Field testId={`${kind}-bank_name`} label="Bank Name" value={form.bank_name} onChange={set("bank_name")} />
            <Field testId={`${kind}-bank_account`} label="Account Number" value={form.bank_account} onChange={set("bank_account")} />
            <Field testId={`${kind}-bank_ifsc`} label="IFSC" value={form.bank_ifsc} onChange={set("bank_ifsc")} />
            {kind === "companies" && <Field testId={`${kind}-bank_branch`} label="Branch" value={form.bank_branch} onChange={set("bank_branch")} />}
          </>}
          {kind === "companies" && <>
            <Field testId={`${kind}-upi_id`} label="UPI ID" value={form.upi_id} onChange={set("upi_id")} />
            <Field testId={`${kind}-authorized_signatory`} label="Authorized Signatory" value={form.authorized_signatory} onChange={set("authorized_signatory")} />
            <Field testId={`${kind}-invoice_prefix`} label="Invoice Prefix" value={form.invoice_prefix} onChange={set("invoice_prefix")} />
            <Field testId={`${kind}-wo_prefix`} label="WO Prefix" value={form.wo_prefix} onChange={set("wo_prefix")} />
          </>}
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid={`${kind}-save`} type="submit" className="dos-btn dos-btn-primary">{isNew ? "Create" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
